export type NewsTag = {
  id: number;
  name: string;
  slug: string;
};

export type NewsArticle = {
  id: number;
  slug?: string | null;
  type?: string | null;
  status?: string | null;
  published?: boolean | null;
  publishAt?: string | null;
  title: string;
  excerpt?: string | null;
  content?: string | null;
  image?: string | null;
  imageAlt?: string | null;
  category?: string | null;
  categoryName?: string | null;
  categorySlug?: string | null;
  author?: string | null;
  authorName?: string | null;
  authorSlug?: string | null;
  authorRoleTitle?: string | null;
  authorAvatar?: string | null;
  sourceUrl?: string | null;
  seoTitle?: string | null;
  seoDescription?: string | null;
  seoImage?: string | null;
  keywords?: string | null;
  publishedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  readingTimeMin?: number | null;
  featured?: boolean | null;
  isFeatured?: boolean | null;
  isBreaking?: boolean | null;
  tags?: NewsTag[];
  /**
   * What to display as the read count. The server decides whether this is the real
   * counter or the simulated one (Admin → Settings → News → simulate views).
   */
  views?: number | null;
};

/** Category banner used when an article has no image of its own. */
const CATEGORY_FALLBACK_IMAGES: Record<string, string> = {
  forex: "https://images.unsplash.com/photo-1611974714658-058e1371734e?auto=format&fit=crop&q=80&w=1200",
  crypto: "https://images.unsplash.com/photo-1518546305927-5a555bb7020d?auto=format&fit=crop&q=80&w=1200",
  stocks: "https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?auto=format&fit=crop&q=80&w=1200",
  commodities: "https://images.unsplash.com/photo-1610375461246-83df859d849d?auto=format&fit=crop&q=80&w=1200",
  economy: "https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?auto=format&fit=crop&q=80&w=1200",
  geopolitic: "https://images.unsplash.com/photo-1529107386315-e1a2ed48a620?auto=format&fit=crop&q=80&w=1200",
  fintech: "https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&q=80&w=1200",
};

const DEFAULT_FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1611974714658-058e1371734e?auto=format&fit=crop&q=80&w=1200";

/**
 * Banner to render for an article. Falls back to a category-appropriate stock image
 * rather than rendering an empty box, so every card keeps the same shape.
 */
export function newsImageFor(a: Partial<NewsArticle>) {
  const own = String(a?.image || "").trim();
  if (own) return own;

  const key = String(a?.categorySlug || a?.categoryName || a?.category || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-");
  if (key && CATEGORY_FALLBACK_IMAGES[key]) return CATEGORY_FALLBACK_IMAGES[key];

  const loose = Object.keys(CATEGORY_FALLBACK_IMAGES).find((k) => key.includes(k));
  return loose ? CATEGORY_FALLBACK_IMAGES[loose] : DEFAULT_FALLBACK_IMAGE;
}

/** Compact view label: 987 · 12.4K · 1.2M */
export function newsViewsLabel(a: Partial<NewsArticle>) {
  const n = Math.max(0, Number(a?.views || 0));
  if (!Number.isFinite(n)) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  return String(Math.round(n));
}

function stripHtmlToWords(input: unknown): string[] {
  return String(input || "")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .split(/\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function newsReadingTimeMin(a: Partial<NewsArticle>, wordsPerMinute = 220): number {
  const words = stripHtmlToWords([a.content, a.excerpt, a.title].filter(Boolean).join(" ")).length;
  if (words > 0) return Math.max(1, Math.ceil(words / wordsPerMinute));
  return Math.max(1, Number(a.readingTimeMin || 1));
}

export type NewsCategory = {
  id: number | null;
  parentId: number | null;
  name: string;
  slug: string;
  description?: string | null;
  articleCount?: number;
};

export function newsHref(a: Partial<NewsArticle>) {
  const slug = String((a as any)?.slug || "").trim();
  if (slug) return `/news/${slug}`;
  const id = Number((a as any)?.id);
  return Number.isFinite(id) ? `/news/${id}` : "/news";
}

export function newsDateLabel(a: Partial<NewsArticle>) {
  const src = (a as any)?.publishedAt || (a as any)?.createdAt;
  if (!src) return "—";
  const d = new Date(src);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
}

/**
 * The timestamp an article is dated by — and it must be the **same** value the
 * server orders the feed by, or the list shows timestamps that jump around and
 * looks broken even when the sort is right.
 *
 * `publishedAt` first: this is the editorial publish timestamp and the value a
 * reader expects to see converted into their own timezone. `createdAt` is only a
 * fallback for older/imported rows that do not have a publish timestamp.
 */
function newsTimestamp(a: Partial<NewsArticle>): Date | null {
  const src = (a as any)?.publishedAt || (a as any)?.createdAt;
  if (!src) return null;
  const d = new Date(src);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Machine-readable timestamp for a `<time dateTime>` attribute.
 *
 * Always UTC/ISO. This is the value crawlers, `NewsArticle` schema and AI
 * systems should read — never a formatted string, which is ambiguous about
 * timezone and cannot be parsed reliably.
 */
export function newsIsoDateTime(a: Partial<NewsArticle>): string | undefined {
  return newsTimestamp(a)?.toISOString();
}

/**
 * The visitor's timezone, e.g. `GMT+6` / `BST`.
 *
 * Passing `undefined` as the locale and omitting `timeZone` is deliberate: both
 * fall back to the device, which is what makes a timestamp render in the
 * reader's own zone. The server-side snapshot cannot do this (it has no idea
 * where the reader is), so it renders UTC and labels it — see
 * `renderNewsSnapshotHtml` in server/routes.ts. Keeping one helper here means
 * the browser and the snapshot can never drift apart in *format*.
 */
export function visitorTimeZoneLabel(d: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" }).formatToParts(d);
    return parts.find((p) => p.type === "timeZoneName")?.value || "";
  } catch {
    return "";
  }
}

/**
 * Exact publish date *and* time in the visitor's timezone: `Sep 26, 2026 · 09:42 AM`.
 *
 * Cards used to print a date only (and the homepage printed a bare
 * `toLocaleDateString()`, so two cards for the same day were indistinguishable).
 * A news site needs the minutes: "SEC faces Sept 27 deadline" is only useful if
 * the reader can see how long ago it was filed.
 */
export function newsDateTimeLabel(a: Partial<NewsArticle>): string {
  const d = newsTimestamp(a);
  if (!d) return "—";

  const date = new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(d);

  const time = new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(d);

  return `${date} · ${time}`;
}

/**
 * Just the clock time, for places that already print the date
 * (the article header prints both, separated by the byline).
 */
export function newsTimeLabel(a: Partial<NewsArticle>): string {
  const d = newsTimestamp(a);
  if (!d) return "—";
  return new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(d);
}

/** True when `updatedAt` is meaningfully later than the publish time. */
export function newsWasUpdated(a: Partial<NewsArticle>): boolean {
  const published = newsTimestamp(a);
  const updated = (a as any)?.updatedAt ? new Date((a as any).updatedAt) : null;
  if (!published || !updated || Number.isNaN(updated.getTime())) return false;
  // A few minutes of drift is the publish pipeline touching the row, not an edit.
  return updated.getTime() - published.getTime() > 5 * 60 * 1000;
}

/** Updated timestamp label, e.g. `Updated Sep 26, 2026 · 11:05 AM`. */
export function newsUpdatedLabel(a: Partial<NewsArticle>): string {
  const updated = (a as any)?.updatedAt ? new Date((a as any).updatedAt) : null;
  if (!updated || Number.isNaN(updated.getTime())) return "";
  const label = newsDateTimeLabel({ publishedAt: updated.toISOString() } as Partial<NewsArticle>);
  return label === "—" ? "" : `Updated ${label}`;
}

/**
 * Newest first, defensively.
 *
 * The API orders by publish timestamp with a `createdAt` fallback. This is the
 * belt that keeps the UI correct even if an older API build is still live.
 */
export function sortNewsLatestFirst<T extends Partial<NewsArticle>>(items: T[]): T[] {
  return [...(items || [])].sort((a, b) => {
    const ta = newsTimestamp(a)?.getTime() ?? 0;
    const tb = newsTimestamp(b)?.getTime() ?? 0;
    if (tb !== ta) return tb - ta;
    return Number((b as any)?.id || 0) - Number((a as any)?.id || 0);
  });
}
